<template>
    <v-container :fluid="true" class="max-height">
        <v-row>
            <v-col class="px-5 d-flex justify-space-between" cols="12">
                <h1 class="text-left">Personal Trackers</h1>
                <div v-if="user.type === 'admin'">
                    <v-btn color="black" prepend-icon="mdi-plus" @click="createDevice">Add</v-btn>
                </div>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="8">
                <v-chip-group v-if="device_counts.length">
                    <v-chip density="comfortable">Total: {{ totalDevices }}</v-chip>
                    <v-chip v-for="device_count in device_counts" density="comfortable">
                        {{ status(device_count.status) }}: {{ device_count.count }}
                    </v-chip>
                </v-chip-group>
            </v-col>
            <v-col class="text-end pe-5">
                <v-btn class="text-capitalize me-2" size="small" @click="filtersDialogSwitch = true">Filters</v-btn>
                <v-menu v-if="Object.keys(activeFilters).length">
                    <template v-slot:activator="{ props }">
                        <v-btn append-icon="mdi-menu-down" size="small" v-bind="props">Active Filters</v-btn>
                    </template>

                    <v-list class="border pa-0" density="compact">
                        <v-list-item v-for="(filter,key) in activeFilters" :key="key" class="py-0 border-b">
                            <div class="d-flex justify-space-between align-center">
                                <span>{{ filter.label }}</span>
                                <v-btn icon="mdi mdi-close" size="small" variant="plain" @click="removeFilter(key)"/>
                            </div>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12">
                <v-table>
                    <thead>
                    <tr>
                        <th></th>
                        <th>Company</th>
                        <th-btn :sortedColumn="sortedColumn" column="label" sortable title="Asset" @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn" column="sku" sortable title="SKU" @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn" column="slno" sortable title="Sl. No." @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn" column="imei" sortable title="IMEI" @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn" column="bt_mac" sortable title="BT MAC" @sort="sort"/>
                        <th-btn :sortedColumn="sortedColumn" column="status" sortable title="Status" @sort="sort"/>
                        <th>Last Reported</th>
                        <th>Address</th>
                        <th>Actions</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="device in devices" v-if="!loading && devices.length" :key="device.id" class="text-left">
                        <td>
                            <div class="d-flex justify-space-between">
                                <v-icon size="small" v-bind="signals(device?.device_status?.signal_bars)"/>
                                <v-icon size="small" v-bind="battery(device?.device_status?.battery1_bars, device?.battery1_type ?? device?.battery2_type)"/>
                                <v-tooltip location="top">
                                    <template v-slot:activator="{ props }">
                                        <v-btn density="compact" flat icon size="small" v-bind="props">
                                            <v-icon
                                                :color="(device.device_status && device.device_status.voltage1)?(device.device_status.voltage1>3.4?'green':'orange'):'red'">
                                                mdi-circle
                                            </v-icon>
                                        </v-btn>
                                    </template>
                                    <span>{{
                                            (device.device_status && device.device_status.voltage1) ? (device.device_status.voltage1 > 3.4 ? 'Full Battery' : 'Medium Battery') : 'Low Battery'
                                        }}</span>
                                </v-tooltip>
                            </div>
                        </td>
                        <td>{{ device.company_name }}</td>
                        <td>{{ device.label }}</td>
                        <td>{{ device.sku }}</td>
                        <td>
                            <router-link :to="`devices/${device.slno}`" target="_blank">{{
                                    device.slno
                                }}
                            </router-link>
                        </td>
                        <td>{{ device.imei }}</td>
                        <td>{{ device.bt_mac }}</td>
                        <td class="text-capitalize">{{ status(device.status) }}</td>
                        <td class="text-sm-body-2">{{
                                device.device_status ?
                                    formatDateTime(device.device_status.last_updated) : ''
                            }}
                        </td>
                        <td class="text-sm-body-2 cursor-pointer">
                            <v-tooltip :text="device?.device_status?.formatted_address" location="top">
                                <template v-slot:activator="{ props }">
                                    <v-chip v-bind="props" variant="text">
                                        <template v-slot:default>
                                            <v-chip density="compact" variant="text"
                                                    @click="openStreetView(device.device_status)">
                                                {{
                                                    device?.device_status?.formatted_address ? (device.device_status.formatted_address.substr(0, 20) + '...') : '-'
                                                }}
                                            </v-chip>
                                        </template>
                                        <template v-slot:append>
                                            <v-btn
                                                class="d-inline ms-2"
                                                density="compact"
                                                icon="mdi-content-copy"
                                                size="small"
                                                variant="text"
                                                @click="copyText(device?.device_status)"
                                            />
                                        </template>
                                    </v-chip>
                                </template>
                            </v-tooltip>
                        </td>
                        <td>
                            <v-btn-group density="compact" divided variant="outlined">
                                <v-btn :disabled="createDialogSwitch" class="text-info" prepend-icon="mdi-content-copy"
                                       size="x-small" @click="copyDevice(device)">
                                    Copy
                                </v-btn>
                                <v-btn :disabled="editDialogSwitch" class="text-warning" prepend-icon="mdi-pencil"
                                       size="x-small" @click="editDevice(device)">Edit
                                </v-btn>
                                <v-btn class="text-error" prepend-icon="mdi-delete" size="x-small"
                                       @click="deleteDevice(device)">Delete
                                </v-btn>
                            </v-btn-group>
                        </td>
                    </tr>
                    <tr v-else>
                        <td v-if="loading" colspan="12">
                            <v-progress-circular color="primary" indeterminate/>
                        </td>
                        <td v-else colspan="12">No record found</td>
                    </tr>
                    </tbody>
                </v-table>
            </v-col>
        </v-row>

        <v-row v-if="totalPages > 1" justify="center">
            <v-col cols="8">
                <v-container class="max-width">
                    <v-pagination
                        v-model="currentPage"
                        :length="totalPages"
                        class="my-4"
                        density="compact"
                        @update:model-value="fetchDevices"
                    ></v-pagination>
                </v-container>
            </v-col>
        </v-row>
    </v-container>

    <Create-Device
        v-if="user.type === 'admin' && createDialogSwitch"
        v-model="createDialogSwitch"
        :device="selectedDevice"
        :device-type="'personal'"
        @addDevice="addDevice"
    />
    <Edit-Device
        v-if="editDialogSwitch"
        v-model="editDialogSwitch"
        :device="selectedDevice"
        :device-type="'personal'"
        @updateDevice="updateDevice"
    />

    <filters-dialog
        v-if="filtersDialogSwitch"
        v-model="filtersDialogSwitch"
        :filters="filters"

        @applyFilters="fetchDevices"
        @resetFilters="clearFilters"
    />
</template>

<script setup>
import {computed, onMounted, onUnmounted, reactive, ref} from "vue";
import {send} from "@/composables/apiClient";
import {useAuthStore} from "@/stores/authStore";
import ThBtn from "@/components/globals/th-btn.vue";
import {useClipboard} from "@/composables/clipboard";
import {formatDateTime} from '@/composables/useTimezone';
import {useSnackbarStore} from "@/stores/snackBarStore";
import EditDevice from "@/components/dialogs/devices/Edit.vue";
import CreateDevice from "@/components/dialogs/devices/Create.vue";
import FiltersDialog from "@/components/cards/devices/FiltersDialog.vue";
import {powerSourceIcon} from "@/composables/usePowerSourceIcon";

const devices = ref([]);
const {user} = useAuthStore();
const loading = ref(true);
const totalPages = ref(1);
const currentPage = ref(1);
const totalDevices = ref(0);
const productSkus = ref([]);
const sortedType = ref(null);
const sortedColumn = ref(null);
const device_counts = ref([]);
const selectedDevice = ref(null);
const {copyToClipboard} = useClipboard();
const {showSnackbar} = useSnackbarStore();
const editDialogSwitch = ref(false);
const createDialogSwitch = ref(false);
const filtersDialogSwitch = ref(false);
const deviceStatuses = ref([
    {id: 0, title: 'Active'},
    {id: 1, title: 'Disabled'},
    {id: 2, title: 'Faulty'},
    {id: 3, title: 'Lost'}
]);
const filters = reactive({
    label: {label: 'Asset Name', data: null},
    serial_num: {label: 'Serial Number', data: null, action: null},
    geo_radius: {label: 'Radius', type: 'number', data: null, latitude: null, longitude: null},
    sku: {label: 'SKU', type: 'select', data: null, title: 'sku', value: 'id', source: productSkus},
    device_status: {
        label: 'Device Status',
        type: 'select',
        data: null,
        title: 'title',
        value: 'id',
        source: deviceStatuses
    },
});
const activeFilters = computed(() => {
    const activatedfilters = {};
    for (const [key, filter] of Object.entries(filters)) {
        if (filter.data !== null) {
            activatedfilters[key] = filter;
        }
    }
    return activatedfilters;
});

onMounted(() => {
    fetchDevices();
    fetchProductSkusList();
});
onUnmounted(() => {
    const localStorageProfiles = JSON.parse(localStorage.getItem('filterProfiles'));
    if (localStorageProfiles?.selectedProfile) {
        localStorage.setItem('filterProfiles', JSON.stringify({
            selectedProfile: null,
            profiles: localStorageProfiles.profiles,
        }));
    }
});

const removeFilter = (key) => {
    filters[key]['data'] = null;
    filters[key]['action'] ? filters[key]['action'] = null : '';
    totalPages.value = 1;
    currentPage.value = 1;
    totalDevices.value = 0;
    sortedType.value = null;
    fetchDevices();
}
const clearFilters = () => {
    Object.keys(filters).forEach((key) => {
        filters[key]['data'] = null;
        filters[key]['action'] ? filters[key]['action'] = null : ''
    });
    totalPages.value = 1;
    currentPage.value = 1;
    totalDevices.value = 0;
    sortedType.value = null;
    fetchDevices();
}
const status = (status) => {
    switch (status) {
        case -1:
            return 'Non Reporting';
        case 0:
            return 'Active';
        case 1:
            return 'Disabled';
        case 2:
            return 'Faulty';
        case 3:
            return 'Lost';
    }
};
const signals = (strength) => {
    switch (strength) {
        case 0:
            return {
                icon: 'mdi-signal-off',
                color: '#dbdbdb'
            }
        case 1:
            return {
                icon: 'mdi-signal-cellular-outline',
                color: '#d70000'
            }
        case 2:
            return {
                icon: 'mdi-signal-cellular-1',
                color: '#ff9e01'
            }
        case 3:
            return {
                icon: 'mdi-signal-cellular-2',
                color: '#849e00'
            }
        case 4:
            return {
                icon: 'mdi-signal-cellular-3',
                color: '#099f00'
            }
        case 5:
            return {
                icon: 'mdi-signal-cellular-3',
                color: '#099f00'
            }
    }
}
const battery = (strength, type = null) => {
    return powerSourceIcon(strength, type)
}
const copyText = (device_status) => {
    if (device_status?.formatted_address
        && device_status.formatted_address !== ''
        && device_status.formatted_address !== 'Address not found') {
        copyToClipboard(device_status?.formatted_address)
    } else {
        copyToClipboard(`${device_status?.latitude}, ${device_status?.longitude}`);
    }
}
const openStreetView = (device_status) => {
    if (device_status?.formatted_address
        && device_status.formatted_address !== ''
        && device_status.formatted_address !== 'Address not found') {
        window.open(`http://maps.google.com/maps/dir//${device_status?.formatted_address ?? ''}`, "_blank");
    } else {
        window.open(`http://maps.google.com/maps/dir//${device_status?.latitude},${device_status?.longitude}`, "_blank")
    }
};
const sort = (sorting) => {
    sortedColumn.value = sorting.column;
    sortedType.value = sorting.order;
    fetchDevices();
}
const fetchProductSkusList = () => {
    send('get', `product-skus`, {
        list: true,
        portals: true,
        columns: JSON.stringify("id,sku")
    })
        .then((response) => {
            productSkus.value = response.data.data.product_skus;
        }).catch((error) => {
        snackbar.showSnackbar('Product skus list could not be loaded', 'error')
    })
}
const fetchDevices = () => {
    let data = {
        page: currentPage.value,
        order: sortedType.value,
        sort: sortedColumn.value,
    };
    loading.value = true;
    if (Object.keys(activeFilters.value).length) {
        send('post', `personal-trackers/list-filters`, {...data, ...filters})
            .then((response) => {
                devices.value = response.data.data.devices.data;
                totalDevices.value = response.data.data.devices.total;
                totalPages.value = response.data.data.devices.last_page;
                currentPage.value = response.data.data.devices.current_page;
                device_counts.value = response.data.data.device_counts;
                loading.value = false;
            })
            .catch((error) => {
                console.log(error)
                showSnackbar('Personal Trackers could not be loaded', 'error')
            })
    } else {
        send('get', `personal-trackers/list`, data)
            .then((response) => {
                devices.value = response.data.data.devices.data;
                totalDevices.value = response.data.data.devices.total;
                totalPages.value = response.data.data.devices.last_page;
                currentPage.value = response.data.data.devices.current_page;
                device_counts.value = response.data.data.device_counts;
                loading.value = false;
            })
            .catch((error) => {
                console.log(error)
                showSnackbar('Personal Trackers could not be loaded', 'error')
            })
    }
}
const createDevice = () => {
    selectedDevice.value = null;
    createDialogSwitch.value = true;
}
const addDevice = (device) => {
    selectedDevice.value = null;
    devices.value.unshift(device);
}
const copyDevice = (device) => {
    selectedDevice.value = device;
    createDialogSwitch.value = true;
}
const editDevice = (device) => {
    selectedDevice.value = device;
    editDialogSwitch.value = true;
}
const updateDevice = (device) => {
    selectedDevice.value = null;
    devices.value.map((comp, index) => {
        if (comp.id === device.id) {
            devices.value[index] = device;
        }
    });
}
const deleteDevice = (device) => {
    if (confirm(`Are you sure to delete "${device.slno}" device?`)) {
        send('delete', `devices/${device.id}`)
            .then((response) => {
                if (response.data.success) {
                    devices.value.map((dev, index) => {
                        if (dev.id === device.id) {
                            devices.value.splice(index, 1);
                        }
                    });
                    showSnackbar(response.data.message);
                }
            })
            .catch((error) => {
                console.log(error)
                showSnackbar(`"${device.slno}" Personal Tracker could not be deleted`, 'error')
            })
    }
}
</script>
